import React, { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { longDateString } from "../../utils";
import useListLayout from "../../hooks/useListLayout";
import Paginator from "../../components/Paginator";
import "../../starek.css";

// Natural, case-insensitive order ("Zone 2" before "Zone 10").
const naturalCompare = (a, b) =>
    String(a ?? "").localeCompare(String(b ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
    });

/**
 * Zone list view, laid out like every other entity list (checkbox column, Name,
 * Description, Activity tooltip, Status, the user's page size with the shared Paginator).
 *
 * Unlike the other lists, zones are not paged on the server: the hierarchy view needs
 * every zone, so the full list is already loaded and search, sort and paging happen
 * here. Sorting runs over the whole list before paging, not just the visible page.
 */
function ZoneListingView({
    items,
    filterText,
    selectedRows,
    onSelectedRowsChange,
    noDataComponent,
    onRowClicked,
}) {
    const [sort, setSort] = useState(null); // { key, direction }
    const [page, setPage] = useState(1);
    const { pageSize } = useListLayout();

    const parentById = useMemo(() => {
        const m = new Map();
        items.forEach((z) => m.set(z.id, z));
        return m;
    }, [items]);
    const parentName = (z) =>
        z.parent_id ? parentById.get(z.parent_id)?.name || "" : "";

    const filteredItems = useMemo(() => {
        const q = (filterText || "").trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (z) =>
                (z.name || "").toLowerCase().includes(q) ||
                (z.description || "").toLowerCase().includes(q),
        );
    }, [items, filterText]);

    const sortedItems = useMemo(() => {
        if (!sort) return filteredItems;
        const value = {
            name: (z) => z.name || "",
            parent: parentName,
            description: (z) => z.description || "",
            updated_at: (z) => z.updated_at || "",
        }[sort.key];
        const dir = sort.direction === "desc" ? -1 : 1;
        return [...filteredItems].sort(
            (a, b) => dir * naturalCompare(value(a), value(b)),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredItems, sort, parentById]);

    const total = sortedItems.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    // Back to page 1 when the search or sort changes; stay in range after deletes.
    useEffect(() => setPage(1), [filterText, sort]);
    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    const currentPageItems = useMemo(
        () => sortedItems.slice((page - 1) * pageSize, page * pageSize),
        [sortedItems, page, pageSize],
    );

    // Selection is per page, like the other lists.
    const changePage = (next) => {
        setPage(next);
        onSelectedRowsChange?.({ selectedRows: [] });
    };

    const isZoneSelected = (zoneId) => selectedRows.some((row) => row.id === zoneId);

    const handleCheckboxChange = (zone) => {
        const newSelectedRows = isZoneSelected(zone.id)
            ? selectedRows.filter((row) => row.id !== zone.id)
            : [...selectedRows, zone];
        onSelectedRowsChange?.({ selectedRows: newSelectedRows });
    };

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    onChange={(e) => {
                        if (e.target.checked) {
                            onSelectedRowsChange?.({
                                selectedRows: [
                                    ...selectedRows,
                                    ...currentPageItems.filter(
                                        (r) => !isZoneSelected(r.id),
                                    ),
                                ],
                            });
                        } else {
                            const pageIds = new Set(currentPageItems.map((r) => r.id));
                            onSelectedRowsChange?.({
                                selectedRows: selectedRows.filter(
                                    (r) => !pageIds.has(r.id),
                                ),
                            });
                        }
                    }}
                    checked={
                        currentPageItems.length > 0 &&
                        currentPageItems.every((row) => isZoneSelected(row.id))
                    }
                    ref={(el) => {
                        if (!el) return;
                        const some = currentPageItems.some((r) => isZoneSelected(r.id));
                        const all =
                            currentPageItems.length > 0 &&
                            currentPageItems.every((r) => isZoneSelected(r.id));
                        el.indeterminate = some && !all;
                    }}
                />
            ),
            width: "60px",
            center: true,
            sortable: false,
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={isZoneSelected(row.id)}
                    onChange={() => handleCheckboxChange(row)}
                    aria-label={`Select ${row.name}`}
                />
            ),
            ignoreRowClick: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            sortField: "name",
        },
        {
            name: "Parent",
            selector: (row) => parentName(row) || "-",
            sortable: true,
            sortField: "parent",
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
            sortField: "description",
            grow: 2,
        },
        {
            name: "Activity",
            selector: (row) => row.updated_at,
            sortable: true,
            sortField: "updated_at",
            width: "250px",
            cell: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <div>
                                Created by {row.created_by?.name || "Unknown"}{" "}
                                at {longDateString(row.created_at)}
                            </div>
                            <div>
                                Modified by {row.updated_by?.name || "Unknown"}{" "}
                                at {longDateString(row.updated_at)}
                            </div>
                        </Tooltip>
                    }
                >
                    <div style={{ fontSize: "0.85rem" }}>
                        {row.updated_by?.name || "Unknown"} at{" "}
                        {longDateString(row.updated_at)}
                    </div>
                </OverlayTrigger>
            ),
        },
        {
            name: "Status",
            width: "90px",
            cell: (r) => (
                <span className={`badge bg-${r.is_active ? "success" : "secondary"}`}>
                    {r.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
    ];

    const conditionalRowStyles = [
        {
            when: (r) => !r.is_active,
            style: { opacity: 0.5, backgroundColor: "#f8f9fa" },
        },
    ];

    return (
        <>
            <DataTable
                columns={columns}
                data={currentPageItems}
                highlightOnHover
                pointerOnHover
                onRowClicked={(row) => onRowClicked?.(row)}
                striped
                responsive
                dense
                // Sorting is done above over the full list, so the table only reports it.
                sortServer
                onSort={(column, direction) =>
                    setSort(column.sortField ? { key: column.sortField, direction } : null)
                }
                noDataComponent={noDataComponent}
                conditionalRowStyles={conditionalRowStyles}
            />
            <Paginator
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                total={total}
                onChange={changePage}
            />
        </>
    );
}

export default ZoneListingView;
